import React, { memo, useEffect, useState, useContext } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import styled from "styled-components";
import { Checkbox } from "@material-ui/core";
import { setDangerouslyInnerHtml } from "../../../helpers/set-inner-html";
import { moduleSettings } from "../../../stores/module-settings.store";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { pendingBookingStore } from "../../../stores/pending-booking.store";
import { GolStripeCallbackResponse } from "../../../types";
import { ErrorMessage } from "../../errors/message-error-view";
import { GolContactInfoFormContext } from "../confirmation-contact-form/gol-contact-form-interfaces";
import {
  ConfirmationFormLayout,
  FormLine,
} from "../confirmation-contact-form/confirmation-contact-form";
import {
  FormControlStyledLabel,
  RadioWrapper,
} from "../confirmation-contact-form/gol-contact-css";
import { Title } from "../../title/title";
import * as Sentry from "@sentry/react";

const CheckoutFormWrapper = styled.form`
  display: flex;
  flex-flow: column wrap;
  width: 610px;

  /* white */
  background-color: ${(props) => props.theme.backgroundColor};

  /* gray light */
  border: 1px solid #f8f8f8;
  box-sizing: border-box;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  border-radius: ${(props) =>
    props.theme.isBackground ? "0 8px 8px 8px" : "8px"};
  padding: 8px;

  &.disabled {
    pointer-events: none;
  }
  @media screen and (max-width: 980px) {
    align-items: center;
    width: auto;
  }
`;

const CheckoutLine = styled(FormLine)`
  .acceptTerms {
    padding: 0 10px;
  }
  @media screen and (max-width: 980px) {
    flex-flow: column;
  }
`;

const BottomInfo = styled.div`
  margin-left: 10px;
  color: ${(props) => props.theme.disabledColor};
  font-size: 14px;
  line-height: 22px;
  display: flex;
`;

const CardNumber = styled(CardNumberElement)`
  border: 1px solid ${(props) => props.theme.legendBorderGray};
  padding: 9px;
  border-radius: 4px;
  flex: 1 2 0%;
  margin 10px;
`;

const CardExpiry = styled(CardExpiryElement)`
  border: 1px solid ${(props) => props.theme.legendBorderGray};
  padding: 9px;
  border-radius: 4px;
  margin 10px;
  flex: 0 2 15%;
`;

const CardCvc = styled(CardCvcElement)`
  border: 1px solid ${(props) => props.theme.legendBorderGray};
  padding: 9px;
  border-radius: 4px;
  flex: 0 2 15%;
  margin 10px;
`;

const CheckoutFormLayout = styled(ConfirmationFormLayout)``;

const Checkout = ({ clazz }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(useLocation().state);
  const [stripeError, setStripeError] = useState({});
  const [clientSecret, setClientSecret] = useState("");
  const stripe = useStripe();
  const elements = useElements();
  const terms = moduleSettings.disclaimerUrl;
  const policy = moduleSettings.privacyPolicyUrl;
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [prepaymentType, setPrepaymentType] = useState("");
  const [stripeToken, setStripeToken] = useState<string | null>(null);
  const [allElementsAreEmpty, setAllElementsAreEmpty] = useState(true);
  const [inValidMode, setInValidMode] = useState(false);
  const product_name = moduleSettings.productName;
  const onTermsCheckedChange = (ev: any, checked: boolean) => {
    setTermsChecked(checked);
    if (checked) {
      setTermsError(false);
    }
  };
  const formContext = useContext(GolContactInfoFormContext);
  const {
    checkContactPptInfoFormValidity,
    setCheckContactInfoFormValidity,
    setValidPptInfo,
  } = formContext;

  useEffect(() => {
    const prepayment = pendingBookingStore.prepayment;
    setClientSecret(prepayment.client_secret);
    setPrepaymentType(prepayment.payment_kind);
  }, []);

  useEffect(() => {
    if (stripeToken && termsChecked) {
      ///!\ disable since webhook is being trigger
      //pendingBookingStore.stripeToken = stripeToken;
      setValidPptInfo(true);
    }
  }, [stripeToken, termsChecked]);

  useEffect(() => {
    if (checkContactPptInfoFormValidity) {
      setInValidMode(true);
      validatePayment();
    }
  }, [checkContactPptInfoFormValidity]);

  useEffect(() => {
    if (error || Object.keys(stripeError).length > 0 || termsError) {
      setCheckContactInfoFormValidity(false);
    }
  }, [error, stripeError, termsError]);

  const handleChange = async (event: any) => {
    // Listen for changes in the CardElement
    // and display any errors as the customer types their card details
    setAllElementsAreEmpty(allElementsAreEmpty && event.empty);
    if (event.error) {
      let updatedErrors = {
        ...stripeError,
        [event.elementType]: event.error.message,
      };
      setStripeError(updatedErrors);
    } else {
      let updatedErrors = { ...stripeError };
      delete updatedErrors[event.elementType];
      setStripeError(updatedErrors);
    }
  };

  const validatePayment = () => {
    if (!termsChecked) {
      setTermsError(true);
      return;
    }

    handleSubmit();
  };

  const handleDeposit = async (): Promise<GolStripeCallbackResponse> => {
    const { error, setupIntent } = await stripe!.confirmCardSetup(
      clientSecret,
      {
        payment_method: {
          card: elements!.getElement(CardNumberElement)!,
        },
      }
    );

    return {
      stripeError: error,
      stripeToken: setupIntent ? setupIntent.id : undefined,
    };
  };

  const handleDownPayment = async (): Promise<GolStripeCallbackResponse> => {
    const { error, paymentIntent } = await stripe!.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: elements!.getElement(CardNumberElement)!,
        },
      }
    );
    return {
      stripeToken: paymentIntent ? paymentIntent.id : undefined,
      stripeError: error,
    };
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setError({ error: "stripe or element not set" });
      setStripeError({ stripe_submit_failed: "stripe or element not set" });
      return;
    }

    localStorage.setItem(
      "pendingBookingId",
      pendingBookingStore.pendingBooking.id
    );
    localStorage.setItem("referralKey", moduleSettings.getReferralKey());

    const { stripeError, stripeToken } =
      "guarantee" === prepaymentType
        ? await handleDeposit()
        : await handleDownPayment();
    setStripeToken(stripeToken ? stripeToken : null);

    localStorage.removeItem("pendingBookingId");
    localStorage.removeItem("referralKey");

    if (stripeError) {
      setError({ error: stripeError!.message });
      setStripeError({ stripe_submit_failed: stripeError!.message });
      Sentry.withScope(function (scope) {
        scope.setTag("StripeError", prepaymentType);
        Sentry.captureException(new Error(stripeError!.message));
      });
    } else {
      setStripeError({});
      setError(null);
    }
  };
  return (
    <CheckoutFormLayout data-test="checkoutForm">
      {allElementsAreEmpty && inValidMode && (
        <ErrorMessage
          error={t(`instabook.prepayment.payment_top`)}
          status={"error"}
        />
      )}
      {Object.keys(stripeError).length > 0 && (
        <ErrorMessage
          error={Object.values(stripeError).join("<br/>")}
          status={"error"}
        />
      )}
      {termsError && (
        <ErrorMessage
          error={t(`instabook.prepayment.accept_terms_unchecked`)}
          status={"error"}
        />
      )}
      <Title text={t("instabook.prepayment.formLabel")} />
      <CheckoutFormWrapper className={clazz}>
        <CheckoutLine>
          <CardNumber onChange={handleChange} />
          <CardExpiry onChange={handleChange} />
          <CardCvc onChange={handleChange} />
        </CheckoutLine>
        <BottomInfo>
          {t("instabook.prepayment.payment_bottom", { product_name })}
        </BottomInfo>
        {/* Show any error that happens when processing the payment */}

        <CheckoutLine className="acceptTerms">
          <RadioWrapper>
            <FormControlStyledLabel
              onChange={onTermsCheckedChange}
              className={termsError ? "error" : ""}
              control={<Checkbox name="termsPpt" />}
              label={
                <span
                  dangerouslySetInnerHTML={setDangerouslyInnerHtml(
                    t,
                    "instabook.prepayment.terms_and_conditions",
                    {
                      terms,
                      policy,
                    }
                  )}
                />
              }
            />
          </RadioWrapper>
        </CheckoutLine>
      </CheckoutFormWrapper>
    </CheckoutFormLayout>
  );
};
export const CheckoutForm = memo(Checkout);
